import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';

export const useSocialStatus = (manualSubscriptions = null, authToken = null) => {
  const { username } = useParams();
  const [statusData, setStatusData] = useState({});
  const [isConnecting, setIsConnecting] = useState(true);
  const [connectionError, setConnectionError] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [subscriptions, setSubscriptions] = useState(manualSubscriptions || {});
  const websocketRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const subscriptionsRef = useRef(subscriptions);

  // Debug logs to track component state
  useEffect(() => {
    console.log("SocialStatus: Subscriptions updated", subscriptions);
  }, [subscriptions]);

  // Keep ref in sync with state
  useEffect(() => {
    subscriptionsRef.current = subscriptions;
  }, [subscriptions]);
  
  // Update subscriptions from manual input or API
  useEffect(() => {
    if (manualSubscriptions && Object.keys(manualSubscriptions).length > 0) {
      console.log("SocialStatus: Using manual subscriptions:", manualSubscriptions);
      setSubscriptions(manualSubscriptions);
    } 
    else if (!manualSubscriptions && username) {
      console.log("SocialStatus: Fetching subscriptions for username:", username);
      const fetchSocialIds = async () => {
        try {
          const timestamp = new Date().getTime();
          const response = await fetch(`https://api.4fingerstudios.com/api/auth/profile/${username}?_=${timestamp}`);
          if (!response.ok) {
            throw new Error('Failed to fetch social IDs');
          }
          const socials = await response.json();
          console.log("SocialStatus: Fetched socials:", socials);
          
          const newSubscriptions = socials.reduce((acc, { platform, socialId }) => {
            if (!acc[platform]) {
              acc[platform] = [];
            }
            acc[platform].push(socialId);
            return acc;
          }, {});
          console.log("SocialStatus: Created subscriptions:", newSubscriptions);
          setSubscriptions(newSubscriptions);
        } catch (error) {
          console.error('Error fetching social IDs:', error);
          setConnectionError('Failed to load social accounts');
        }
      };
      fetchSocialIds();
    }
  }, [username, manualSubscriptions, authToken]);

  const getWebSocketUrl = useCallback(() => {
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = `${wsProtocol}//api.4fingerstudios.com/api/social/ws`;
    console.log("SocialStatus: WebSocket URL:", wsUrl);
    return wsUrl;
  }, []);

  // Send subscriptions to the server
  const sendSubscriptions = useCallback(() => {
    if (!websocketRef.current || websocketRef.current.readyState !== WebSocket.OPEN) {
      console.log("SocialStatus: Cannot send subscriptions - WebSocket not open");
      return;
    }

    try {
      console.log("SocialStatus: Sending subscriptions:", subscriptionsRef.current);
      const subscribeMsg = {
        type: 'subscribe',
        subscriptions: subscriptionsRef.current,
      };
      websocketRef.current.send(JSON.stringify(subscribeMsg));
    } catch (error) {
      console.error('Error sending subscription message:', error);
    }
  }, []);

  // Connect to WebSocket server
  const connectWebSocket = useCallback(() => {
    // Skip connection if we've already verified there are no subscriptions
    if (Object.keys(subscriptionsRef.current).length === 0) {
      console.log("SocialStatus: No subscriptions to listen for, skipping connection");
      setIsConnecting(false);
      return;
    }
    
    // Clear any existing reconnect timeout
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    // Close existing connection if open
    if (websocketRef.current && websocketRef.current.readyState !== WebSocket.CLOSED) {
      try {
        console.log("SocialStatus: Closing existing WebSocket connection");
        websocketRef.current.onclose = null; // Remove onclose handler to prevent reconnection loop
        websocketRef.current.close();
      } catch (err) {
        console.error("Error closing existing WebSocket:", err);
      }
    }

    try {
      setIsConnecting(true);
      setConnectionError(null);
      setIsConnected(false);
      
      const url = getWebSocketUrl();
      console.log('SocialStatus: Connecting to WebSocket URL:', url);
      
      const ws = new WebSocket(url);
      websocketRef.current = ws;
      
      let pingIntervalId = null;
      
      ws.onopen = () => {
        console.log('SocialStatus: WebSocket connection established');
        setIsConnecting(false);
        setIsConnected(true);
        reconnectAttemptsRef.current = 0;
        
        if (Object.keys(subscriptionsRef.current).length > 0) {
          console.log("SocialStatus: Sending subscriptions after connect");
          sendSubscriptions();
        }
        
        pingIntervalId = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            try {
              ws.send(JSON.stringify({ type: 'ping' }));
            } catch (error) {
              console.error('Error sending ping:', error);
              clearInterval(pingIntervalId);
            }
          } else {
            clearInterval(pingIntervalId);
          }
        }, 30000);
      };
      
      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          console.log("SocialStatus: Received message:", message);
          
          const normalizedMessage = {
            type: message.type?.toLowerCase(),
            platform: (message.platform || message.Platform || '').toLowerCase(),
            userId: message.userId || message.UserId,
            data: message.data || message.Data,
            subscriptions: message.subscriptions || message.Subscriptions,
            message: message.message || message.Message,
          };
          
          switch (normalizedMessage.type) {
            case 'welcome':
              console.log('SocialStatus: Received welcome message:', normalizedMessage);
              break;
            
            case 'subscription_confirmed':
              console.log('SocialStatus: Subscription confirmed:', normalizedMessage.subscriptions);
              break;
            
            case 'status_update':
              console.log(`SocialStatus: Received ${normalizedMessage.platform} status update for ${normalizedMessage.userId}`);
              
              setStatusData(prevData => ({
                ...prevData,
                [normalizedMessage.platform]: {
                  ...(prevData[normalizedMessage.platform] || {}),
                  [normalizedMessage.userId]: normalizedMessage.data,
                },
              }));
              break;
            
            case 'pong':
              break;
            
            case 'error':
              console.error('SocialStatus: WebSocket error message:', normalizedMessage.message);
              setConnectionError(normalizedMessage.message);
              break;
            
            default:
              console.log('SocialStatus: Unknown message type:', normalizedMessage);
          }
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };
      
      ws.onclose = (event) => {
        console.log('SocialStatus: WebSocket connection closed:', event.code, event.reason);
        setIsConnected(false);
        
        if (pingIntervalId) {
          clearInterval(pingIntervalId);
          pingIntervalId = null;
        }
        
        if (event.code !== 1000) {
          const attempt = reconnectAttemptsRef.current + 1;
          reconnectAttemptsRef.current = attempt;
          
          const reconnectDelay = Math.min(10000, 1000 * Math.pow(1.5, Math.min(6, attempt)));
          
          console.log(`SocialStatus: Attempting to reconnect in ${reconnectDelay}ms (attempt ${attempt})`);
          
          reconnectTimeoutRef.current = setTimeout(() => {
            if (websocketRef.current) {
              connectWebSocket();
            }
          }, reconnectDelay);
        }
      };
      
      ws.onerror = (error) => {
        console.error('SocialStatus: WebSocket error:', error);
        setConnectionError('Connection error. Will try to reconnect...');
      };
    } catch (error) {
      console.error('SocialStatus: Error setting up WebSocket:', error);
      setConnectionError(`Failed to connect: ${error.message}`);
      setIsConnecting(false);
      setIsConnected(false);
      
      reconnectTimeoutRef.current = setTimeout(() => {
        connectWebSocket();
      }, 5000);
    }
  }, [getWebSocketUrl, sendSubscriptions]);

  // Effect to monitor subscription changes and trigger connection
  useEffect(() => {
    const hasSubscriptions = Object.keys(subscriptions).length > 0;
    
    console.log("SocialStatus: Connection effect triggered");
    console.log("SocialStatus: Has subscriptions:", hasSubscriptions);
    
    if (hasSubscriptions) {
      console.log("SocialStatus: Has subscriptions, connecting...");
      connectWebSocket();
    } else {
      console.log("SocialStatus: No subscriptions, skipping connection");
      setIsConnecting(false);
    }
    
    return () => {
      console.log("SocialStatus: Cleaning up connection effect");
      
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      
      if (websocketRef.current) {
        console.log("SocialStatus: Closing WebSocket on cleanup");
        
        if (websocketRef.current.readyState === WebSocket.OPEN) {
          try {
            const unsubscribeMsg = {
              type: 'unsubscribe',
              subscriptions: subscriptionsRef.current,
            };
            websocketRef.current.send(JSON.stringify(unsubscribeMsg));
          } catch (error) {
            console.error('Error sending unsubscribe message:', error);
          }
          
          websocketRef.current.onclose = null;
          websocketRef.current.onerror = null;
          websocketRef.current.onmessage = null;
          websocketRef.current.onopen = null;
          
          websocketRef.current.close();
        }
        
        websocketRef.current = null;
      }
    };
  }, [subscriptions, connectWebSocket]);

  useEffect(() => {
    if (isConnected && Object.keys(subscriptions).length > 0) {
      console.log("SocialStatus: Connected and has subscriptions, sending subscriptions");
      sendSubscriptions();
    }
  }, [isConnected, subscriptions, sendSubscriptions]);

  return {
    statusData,
    isConnecting,
    isConnected,
    connectionError,
    reconnect: connectWebSocket,
  };
};